import { eventChannel } from 'redux-saga';
import { call, cancelled, put, take } from 'redux-saga/effects';
import 'signalr';
import { EnvironmentService } from "../../common";
import { photoCaptureIntelligentDamageConnectionFailureAction, photoCaptureIntelligentDamageConnectionSuccessAction, photoCaptureIntelligentDamageNotificationReceivedAction } from '../actions';
import { IntelligentPhotoCaptureSignalRConnectionEventType } from '../enums';
import { intelligentPhotoCaptureDamageConnectionConstants } from '../constants';
export const createSignalRHubConnection = () => {
    const baseUrl = EnvironmentService.getEnvironment().wcRealTimeNotificationUrl;
    const signalRHubUrl = `${baseUrl}WCRealTimeNotificationsApi/signalr`;
    const accessToken = localStorage.getItem('accessToken');
    return jQuery.hubConnection(signalRHubUrl, {
        useDefaultPath: false,
        qs: accessToken ? `token=${accessToken}` : ''
    });
};
export const createPhotoCaptureSignalRConnectionEventChannel = (connection, hubProxy) => {
    return eventChannel(emit => {
        const onReceiveNotification = (notification) => {
            const normalizedNotification = {
                connectionId: notification.ConnectionId || '',
                requestId: notification.RequestId || '',
                referenceId: notification.ReferenceId || '',
                claimNumber: notification.ClaimNumber || '',
                sourceApplication: notification.SourceApplication || '',
                notificationType: notification.NotificationType || '',
                notificationCategory: notification.NotificationCategory || '',
                title: notification.Title || '',
                message: notification.Message || '',
                timestamp: notification.Timestamp || ''
            };
            emit({
                type: IntelligentPhotoCaptureSignalRConnectionEventType.RECEIVE_NOTIFICATION,
                notification: normalizedNotification
            });
        };
        hubProxy.on(intelligentPhotoCaptureDamageConnectionConstants.DAMAGE_RECEIVE_NOTIFICATION_EVENT, onReceiveNotification);
        if (connection.reconnected) {
            connection.reconnected(() => {
                emit({
                    type: IntelligentPhotoCaptureSignalRConnectionEventType.RECONNECTED,
                    connectionId: connection.id || undefined
                });
            });
        }
        if (connection.disconnected) {
            connection.disconnected(() => {
                const isManualStop = !!connection.__isManualStop;
                emit({
                    type: IntelligentPhotoCaptureSignalRConnectionEventType.DISCONNECTED,
                    message: isManualStop
                        ? intelligentPhotoCaptureDamageConnectionConstants.SIGNALR_MANUAL_STOP_MESSAGE
                        : intelligentPhotoCaptureDamageConnectionConstants.SIGNALR_DISCONNECTED_MESSAGE
                });
            });
        }
        if (connection.error) {
            connection.error((error) => {
                emit({
                    type: IntelligentPhotoCaptureSignalRConnectionEventType.ERROR,
                    message: (error === null || error === void 0 ? void 0 : error.message) || intelligentPhotoCaptureDamageConnectionConstants.SIGNALR_ERROR_MESSAGE
                });
            });
        }
        return () => {
            hubProxy.off(intelligentPhotoCaptureDamageConnectionConstants.DAMAGE_RECEIVE_NOTIFICATION_EVENT, onReceiveNotification);
        };
    });
};
export function* watchPhotoCaptureSignalRConnectionEventsSaga(connectionEvents) {
    try {
        while (true) {
            const connectionEvent = yield take(connectionEvents);
            switch (connectionEvent.type) {
                case IntelligentPhotoCaptureSignalRConnectionEventType.RECONNECTED:
                    if (connectionEvent.connectionId) {
                        yield put(photoCaptureIntelligentDamageConnectionSuccessAction(connectionEvent.connectionId));
                    }
                    break;
                case IntelligentPhotoCaptureSignalRConnectionEventType.RECEIVE_NOTIFICATION:
                    if (connectionEvent.notification) {
                        yield put(photoCaptureIntelligentDamageNotificationReceivedAction(connectionEvent.notification));
                        if (connectionEvent.notification.connectionId) {
                            yield put(photoCaptureIntelligentDamageConnectionSuccessAction(connectionEvent.notification.connectionId));
                        }
                    }
                    break;
                case IntelligentPhotoCaptureSignalRConnectionEventType.DISCONNECTED:
                    if (connectionEvent.message === intelligentPhotoCaptureDamageConnectionConstants.SIGNALR_MANUAL_STOP_MESSAGE) {
                        break;
                    }
                    yield put(photoCaptureIntelligentDamageConnectionFailureAction(connectionEvent.message || intelligentPhotoCaptureDamageConnectionConstants.SIGNALR_DISCONNECTED_MESSAGE));
                    break;
                case IntelligentPhotoCaptureSignalRConnectionEventType.ERROR:
                    break;
                default:
                    break;
            }
        }
    }
    finally {
        if (yield cancelled()) {
            connectionEvents.close();
        }
    }
}
export function* getInitializedDamageConnectionArtifactsSaga(signalRService) {
    const signalRConnection = (yield call([
        signalRService,
        signalRService.getActiveDamageConnection
    ]));
    if (!signalRConnection) {
        throw new Error(intelligentPhotoCaptureDamageConnectionConstants.SIGNALR_UNAVAILABLE_AFTER_NEGOTIATION_MESSAGE);
    }
    yield call([signalRService, signalRService.initializeDamageHubProxy], intelligentPhotoCaptureDamageConnectionConstants.DAMAGE_NOTIFICATION_HUB);
    const signalRHubProxy = (yield call([
        signalRService,
        signalRService.getActiveDamageHubProxy
    ]));
    if (!signalRHubProxy) {
        throw new Error(intelligentPhotoCaptureDamageConnectionConstants.SIGNALR_HUB_PROXY_UNAVAILABLE_MESSAGE);
    }
    const signalRConnectionEvents = (yield call(createPhotoCaptureSignalRConnectionEventChannel, signalRConnection, signalRHubProxy));
    if (signalRConnection.state !== intelligentPhotoCaptureDamageConnectionConstants.SIGNALR_CONNECTED_STATE) {
        try {
            yield call((connection) => connection.start(), signalRConnection);
        }
        catch (error) {
            signalRConnectionEvents.close();
            throw error;
        }
    }
    const connectionId = signalRConnection.id || '';
    if (!connectionId) {
        signalRConnectionEvents.close();
        throw new Error(intelligentPhotoCaptureDamageConnectionConstants.SIGNALR_CONNECTION_ID_UNAVAILABLE_MESSAGE);
    }
    return {
        signalRConnectionEvents,
        connectionId
    };
}
//# sourceMappingURL=IntelligentPhotoCaptureDamageConnectionHelper.js.map